/**
 * Строки панели быстрых действий: все аккаунты набора, сгруппированные по
 * когортам.
 *
 * Модуль ЧИСТЫЙ по правилу lib/*.ts: здесь решается, от чьего имени уйдёт
 * нажатие в каждой строке и почему какая-то из них не покупает, — это
 * проверяется тестом.
 *
 * # Зачем строки, а не одна цель на панель
 *
 * Замечание владельца 2026-10-07: «быстрые» действия перестают быть быстрыми,
 * если перед каждым нажатием надо переключить аккаунт в селекторе. Поэтому
 * каждый аккаунт и каждая когорта — своя строка со своими кнопками, а цель
 * нажатия — строка, в которой нажали. Общий выбор остался только у формы
 * ордера (settings.formTarget) и у «активной строки» — от неё бьют клавиши.
 *
 * # Порядок и группировка
 *
 * Когорты — в порядке списка когорт; под строкой когорты — строки её
 * аккаунтов. Аккаунт из двух когорт стоит под каждой: группировка показывает
 * когорты как они есть, а нажатие в строке аккаунта одинаково, под какой бы
 * когортой она ни стояла. Аккаунты вне когорт — под заголовком «без когорты»;
 * когорт нет вовсе — просто список аккаунтов, без заголовков.
 *
 * # Симуляция
 *
 * Пробный ордер трейдер принимает от любого аккаунта набора — без ключей и
 * при осушении (paperAccount): денег он не тратит и на биржу не ходит. Поэтому
 * в симуляции покупка и закрытие открыты у всех членов состава, иначе на
 * стенде без загруженных кошельков симуляция молчала бы целиком. Снятие и в
 * симуляции настоящее, и его причина — своя, cancelProblem.
 */

import {
  accountHint,
  accountOf,
  accountsWord,
  placeEligible,
  shortFunder,
  type TargetChoice,
} from "./accounts";
import type { AccountCohort, AccountsResponse, CommandTarget } from "../api/types";

export interface QuickRow {
  /** Ключ строки для React: аккаунт под двумя когортами — две строки. */
  key: string;
  /** `group` — только заголовок «без когорты», без кнопок. */
  kind: "cohort" | "account" | "group";
  /** Слот аккаунта или имя когорты. */
  label: string;
  /** Чьи позиции показывать и с чьих кошельков закрывать. */
  accounts: string[];
  /** Цель покупки и снятия; null — у заголовка группы. */
  target: CommandTarget | null;
  /** Выбор, которым строка становится активной; null — у заголовка. */
  choice: TargetChoice | null;
  /** Строка стоит под заголовком когорты или группы. */
  nested: boolean;
  /** Почему строка не покупает; null — покупает. */
  buyProblem: string | null;
  /** Почему строка не закрывает; null — может. */
  sellProblem: string | null;
  /** Почему строка не снимает ордера; null — может. */
  cancelProblem: string | null;
  /** Короткая пометка у имени: «осушается», «×2». */
  note: string | null;
}

/**
 * Строки панели из состава и когорт.
 *
 * Состав неизвестен — аккаунтов, о которых можно что-то сказать, нет, и
 * строк нет: панель называет причину сама. Когорты ещё не прочитаны — строки
 * аккаунтов без группировки, чтобы торговать можно было, не дожидаясь базы.
 */
export function quickRows(
  accounts: AccountsResponse | null | undefined,
  cohorts: AccountCohort[] | null | undefined,
  paper = false,
): QuickRow[] {
  const roster = accounts?.accounts ?? [];
  if (roster.length === 0) return [];
  const list = cohorts ?? [];
  const out: QuickRow[] = [];
  const grouped = new Set<string>();

  for (const c of list) {
    // Член вне состава в строку не попадает: web_api его позицию не спросит, а
    // трейдер не исполнит. Раскрытие когорты на стороне web_api его тоже
    // пропустит — строка показывает то же, что сделает нажатие.
    const members = c.accounts.filter((f) => accountOf(accounts, f) !== null);
    const eligible = members.filter((f) => {
      const row = accountOf(accounts, f);
      return row !== null && placeEligible(row);
    });
    const loaded = members.filter((f) => accountOf(accounts, f)?.loaded !== false);
    const cancelProblem = loaded.length > 0 ? null : "в когорте нет загруженных трейдером аккаунтов";
    // Пробу web_api раскрывает на всех членов состава — пометка обязана
    // назвать то же число, что уйдёт.
    const placing = paper ? members : eligible;
    out.push({
      key: `c:${c.name}`,
      kind: "cohort",
      label: c.name,
      accounts: members,
      target: { account_cohort: c.name },
      choice: { kind: "cohort", name: c.name },
      nested: false,
      buyProblem:
        placing.length > 0
          ? null
          : members.length === 0
            ? "в когорте нет аккаунтов из состава"
            : "в когорте нет аккаунтов для постановки",
      sellProblem: paper ? (members.length > 0 ? null : "в когорте нет аккаунтов из состава") : cancelProblem,
      cancelProblem,
      // Сколько аккаунтов поставят полный пресет: «$5» на кнопке когорты из
      // двух — это $10 риска, и число обязано стоять рядом с кнопкой.
      note: `×${placing.length}`,
    });
    for (const f of members) {
      grouped.add(f);
      out.push(accountRow(accounts, f, `a:${c.name}:${f}`, true, paper));
    }
  }

  const loose = roster.filter((a) => !grouped.has(a.funder));
  if (loose.length > 0 && list.length > 0) {
    out.push({
      key: "g:loose",
      kind: "group",
      label: "без когорты",
      accounts: loose.map((a) => a.funder),
      target: null,
      choice: null,
      nested: false,
      buyProblem: null,
      sellProblem: null,
      cancelProblem: null,
      note: accountsWord(loose.length),
    });
  }
  for (const a of loose) {
    out.push(accountRow(accounts, a.funder, `a::${a.funder}`, list.length > 0, paper));
  }
  return out;
}

function accountRow(
  accounts: AccountsResponse | null | undefined,
  funder: string,
  key: string,
  nested: boolean,
  paper: boolean,
): QuickRow {
  const row = accountOf(accounts, funder);
  const hint = row ? accountHint(row) : "вне состава";
  const cancelProblem = row?.loaded === false ? (hint ?? "не загружен трейдером") : null;
  return {
    key,
    kind: "account",
    label: row?.slot ?? shortFunder(funder),
    accounts: [funder],
    target: { account: funder },
    choice: { kind: "account", funder },
    nested,
    // Покупка — только у загруженного и применённо включённого: осушаемый
    // продаёт и снимает, но не покупает (Д-2).
    buyProblem: row && (paper || placeEligible(row)) ? null : (hint ?? "не годен для постановки"),
    sellProblem: paper ? (row ? null : "вне состава") : cancelProblem,
    cancelProblem,
    note: hint,
  };
}

/** Совпадает ли строка с выбором оператора (активная строка). */
export function rowMatches(row: QuickRow, choice: TargetChoice | null): boolean {
  if (!choice || !row.choice) return false;
  if (choice.kind === "account") {
    return row.choice.kind === "account" && row.choice.funder === choice.funder;
  }
  return row.choice.kind === "cohort" && row.choice.name === choice.name;
}

/** Строка активного выбора; аккаунт под двумя когортами — первая. */
export function rowOfChoice(rows: readonly QuickRow[], choice: TargetChoice | null): QuickRow | null {
  return rows.find((r) => rowMatches(r, choice)) ?? null;
}

/** Все аккаунты строк — по одному разу, для запросов позиций. */
export function rowAccounts(rows: readonly QuickRow[]): string[] {
  const seen = new Set<string>();
  for (const r of rows) {
    if (r.kind !== "group") for (const a of r.accounts) seen.add(a);
  }
  return [...seen];
}
